import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { canEditItems } from "@/lib/roles";
import { MagnifyingGlass, ArrowRight, CaretUp, CaretDown, PencilSimple } from "@phosphor-icons/react";

const DIET_TYPES = ["Wellness", "Vegetarian", "Bland", "ESR", "CKD3", "Pescatarian", "GDM", "Well+"];

const COLUMNS = [
  { key: "diet_type", label: "Diet Type" },
  { key: "diet_code", label: "Diet Code" },
  { key: "cycle_week", label: "Cycle Wk" },
  { key: "cycle_position", label: "Position" },
  { key: "location", label: "Location" },
  { key: "cost", label: "Cost" },
  { key: "active", label: "Status" },
];

export default function MealItems() {
  const { user } = useAuth();
  const [meals, setMeals] = useState([]);
  const [q, setQ] = useState("");
  const [dietType, setDietType] = useState("");
  const [cycleWeek, setCycleWeek] = useState("");
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: "diet_type", dir: "asc" });
  const canEdit = canEditItems(user);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (dietType) params.set("diet_type", dietType);
      if (cycleWeek) params.set("cycle_week", cycleWeek);
      params.set("limit", "500");
      const { data } = await api.get(`/meals?${params.toString()}`);
      setMeals(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, dietType, cycleWeek]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const sorted = useMemo(() => {
    const arr = [...meals];
    arr.sort((a, b) => {
      const va = (a[sort.key] ?? "").toString().toLowerCase();
      const vb = (b[sort.key] ?? "").toString().toLowerCase();
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [meals, sort]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const SortIcon = ({ col }) => sort.key !== col ? null : (sort.dir === "asc" ? <CaretUp size={10} className="inline ml-1" weight="bold" /> : <CaretDown size={10} className="inline ml-1" weight="bold" />);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4">
        <div className="label-tech mb-1">Catalog</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Meal Items</h1>
      </div>

      <div className="card-flat border-border p-3 mb-4 grid grid-cols-1 md:grid-cols-[1fr_180px_140px] gap-2">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-3.5 text-muted-foreground" />
          <input
            data-testid="meal-items-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by Diet Code or Diet Type…"
            className="w-full h-11 pl-9 pr-3 border border-border bg-background rounded-sm outline-none focus:ring-2 focus:ring-primary text-sm"
          />
        </div>
        <select
          data-testid="meal-items-diet-type"
          value={dietType}
          onChange={(e) => setDietType(e.target.value)}
          className="h-11 px-2 border border-border bg-background rounded-sm text-sm"
        >
          <option value="">All diet types</option>
          {DIET_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
        </select>
        <select
          data-testid="meal-items-cycle-week"
          value={cycleWeek}
          onChange={(e) => setCycleWeek(e.target.value)}
          className="h-11 px-2 border border-border bg-background rounded-sm text-sm"
        >
          <option value="">All weeks</option>
          {[1, 2, 3, 4, 5].map((w) => (<option key={w} value={w}>Week {w}</option>))}
        </select>
      </div>

      <div className="card-flat border-border overflow-hidden" data-testid="meal-items-list">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 border-b border-border sticky top-0 z-10">
              <tr className="text-left label-tech">
                {COLUMNS.map((c) => (
                  <th key={c.key} className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort(c.key)}>
                    {c.label}<SortIcon col={c.key} />
                  </th>
                ))}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.map((m) => (
                <tr key={m.meal_record_id} className="hover:bg-muted/40">
                  <td className="px-3 py-2.5">{m.diet_type}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{m.diet_code}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{m.cycle_week ?? "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{m.cycle_position ?? "—"}</td>
                  <td className="px-3 py-2.5 text-xs truncate max-w-[18ch]">{m.location || <span className="text-muted-foreground italic">—</span>}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">
                    {m.cost === null || m.cost === undefined || m.cost === ""
                      ? <span className="italic text-muted-foreground">Not Set</span>
                      : `$${Number(m.cost).toFixed(4)}`}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {m.active === false ? (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Inactive</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-success">Active</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Link
                      to={`/meal-items/${m.meal_record_id}${canEdit ? "?edit=1" : ""}`}
                      data-testid={`meal-item-open-${m.meal_record_id}`}
                      className="text-primary hover:underline text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1"
                    >
                      {canEdit ? (<><PencilSimple size={12} weight="bold" /> Edit</>) : (<>Detail <ArrowRight size={12} weight="bold" /></>)}
                    </Link>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && !loading && (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">No meal items match.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={8} className="p-4 text-center text-muted-foreground text-xs font-mono">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
